import React, { useState, useEffect } from 'react';
import {
  MoreVertical,
  Users,
  Database,
  Copy,
  Check,
  Calendar,
  Edit,
  Trash2,
  Plus,
  ExternalLink,
  Sparkles,
  Network,
  Building2,
  Loader2,
  RefreshCw,
  Tag,
  Settings,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import UsersModal from './UsersModal';
import ImportTomasModal from './ImportTomasModal';
import EditClientModal from './EditClientModal';
import TagsProcessingModal from './TagsProcessingModal';
import ColumnConfigModal from './ColumnConfigModal';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useClient } from '../../contexts/ClientContext';
import api from '../../utils/api';
import { buildClientSelection } from '../../utils/clientSelection';

interface ClientCardProps {
  client: {
    uid: string;
    name: string;
    display_name?: string;
    database?: {
      host: string;
      port?: number;
      user: string;
      database: string;
      password: string;
    };
    logo?: string;
    logoHeight?: number;
    welcomeVideo?: string;
    greetingVideo?: string;
    userCount?: number;
    tableCount?: number;
    tableError?: boolean;
    tableErrorMessage?: string;
    lastTomaDate?: string | null;
    last_sync_at?: string | null;
    catalogadoPrompt?: string;
    client_type?: string;
    enable_tags?: boolean;
    enable_sku?: boolean;
  };
  onRefresh: () => void;
  onClientPatch?: (uid: string, patch: Record<string, any>) => void;
  onVideoUpload?: (file: File, clientId: string, videoType: 'welcomeVideo' | 'greetingVideo') => void;
  uploadingVideo?: string | null;
  uploadingGreetingVideo?: string | null;
  panelSyncing?: boolean;
  autoOpenEdit?: boolean;
}

interface TomaData {
  tomaNumber: number;
  tableName: string;
  fecha: string | null;
  recordCount: number;
  catalogadoCount: number;
  estado?: string;
}

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onRefresh,
  onClientPatch,
  onVideoUpload,
  uploadingVideo,
  uploadingGreetingVideo,
  panelSyncing,
  autoOpenEdit
}) => {
  const { t } = useTranslation();
  const { setSelectedClient, setIsInAdminMode } = useClient();

  // State
  const [menuOpen, setMenuOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  // Modals
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [importTomasModalOpen, setImportTomasModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  // Auto-open Edit modal cuando llega de "Config Client" del topbar
  useEffect(() => {
    if (autoOpenEdit) setEditModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenEdit]);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [columnConfigModalOpen, setColumnConfigModalOpen] = useState(false);

  // Data
  const [userCount, setUserCount] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [tomas, setTomas] = useState<TomaData[]>([]);
  const [loadingTomas, setLoadingTomas] = useState(false);
  const [loadingMoreTomas, setLoadingMoreTomas] = useState(false);
  const [lastTomaDate, setLastTomaDate] = useState<Date | null>(null);
  const [tomasCount, setTomasCount] = useState<number>(client.tableCount || 0);
  const [totalTomas, setTotalTomas] = useState<number>(client.tableCount || 0);
  const [hasMoreTomas, setHasMoreTomas] = useState<boolean>(false);
  const [syncingNow, setSyncingNow] = useState(false);

  const handleSyncNow = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (syncingNow) return;
    try {
      setSyncingNow(true);
      const res = await api.post(`/api/clients/${client.uid}/sync-tomas`, {}, {
        headers: { 'x-client-id': client.uid }
      });
      if (res.data?.success) {
        if (onClientPatch) {
          onClientPatch(client.uid, { last_sync_at: new Date().toISOString() });
        } else {
          onRefresh();
        }
      }
    } catch (err: any) {
      console.error('Sync now failed:', err);
    } finally {
      setSyncingNow(false);
    }
  };
  const TOMAS_PAGE_SIZE = 5;

  // Initialize from parent data and fetch user count
  useEffect(() => {
    setTomas([]);
    setTomasCount(client.tableCount || 0);
    setTotalTomas(client.tableCount || 0);
    setHasMoreTomas((client.tableCount || 0) > TOMAS_PAGE_SIZE);
    if (client.lastTomaDate) {
      setLastTomaDate(new Date(client.lastTomaDate));
    } else {
      setLastTomaDate(null);
    }
    fetchUserCount();
  }, [client.uid]);

  useEffect(() => {
    if (client.tableCount !== undefined) {
      setTomasCount(client.tableCount);
      setTotalTomas(client.tableCount);
      setHasMoreTomas(client.tableCount > TOMAS_PAGE_SIZE);
    }
  }, [client.tableCount]);

  useEffect(() => {
    if (client.lastTomaDate) {
      setLastTomaDate(new Date(client.lastTomaDate));
    }
  }, [client.lastTomaDate]);

  const fetchUserCount = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('assigned_clients', 'array-contains', client.uid));
      const snapshot = await getDocs(q);
      const usersList = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));

      // Sort users by user_type: superadmin, admin, user
      const sortedUsers = usersList.sort((a: any, b: any) => {
        const typeOrder: { [key: string]: number } = { superadmin: 0, admin: 1, user: 2 };
        const orderA = typeOrder[a.user_type] ?? 3;
        const orderB = typeOrder[b.user_type] ?? 3;
        return orderA - orderB;
      });

      setUsers(sortedUsers);
      setUserCount(snapshot.size);
    } catch (error) {
      console.error('Error fetching user count:', error);
      setUserCount(0);
      setUsers([]);
    }
  };

  const fetchTomasFromFirestore = async () => {
    try {
      const docRef = doc(db, 'client_tomas', client.uid);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) {
        setTomasCount(0);
        setTotalTomas(0);
        setHasMoreTomas(false);
        setLastTomaDate(null);
        return;
      }
      const data = snapshot.data();
      const tomasArray = Array.isArray(data.tomas) ? data.tomas : [];
      setTomasCount(tomasArray.length);
      setTotalTomas(tomasArray.length);
      setHasMoreTomas(tomasArray.length > TOMAS_PAGE_SIZE);

      if (data.lastTomaDate) {
        setLastTomaDate(new Date(data.lastTomaDate));
      } else {
        setLastTomaDate(null);
      }

      const mappedTomas: TomaData[] = tomasArray.map((item: any) => ({
        tomaNumber: item.tomaNumber ?? item.numero ?? item.id ?? null,
        tableName: item.tableName ?? item.nombre ?? null,
        fecha: item.fecha ? new Date(item.fecha).toISOString() : null,
        recordCount: item.recordCount ?? 0,
        catalogadoCount: item.catalogadoCount ?? 0
      }));

      if (mappedTomas.length > 0) {
        setTomas(mappedTomas);
      }
    } catch (firestoreError) {
      console.error('Error fetching tomas from Firestore fallback:', firestoreError);
      setTomasCount(0);
      setTotalTomas(0);
      setHasMoreTomas(false);
      setLastTomaDate(null);
    }
  };

  const fetchBasicTomasInfo = async () => {
    try {
      // Fetch summary from detailed endpoint when backend is available
      let response;
      try {
        response = await api.get(`/api/clients/${client.uid}/tomas?detailed=true&limit=1`);
      } catch (networkError) {
        console.warn('Detailed tomas endpoint unavailable, falling back to cached data.', networkError);
        response = await api.get(`/api/clients/${client.uid}/tomas`);
      }

      if (response.data.success) {
        const totalFromResponse =
          typeof response.data.totalTomas === 'number' ? response.data.totalTomas : null;
        const pagedCount = Array.isArray(response.data.tomas) ? response.data.tomas.length : 0;
        const resolvedCount = totalFromResponse ?? pagedCount ?? 0;

        setTomasCount(resolvedCount);
        setTotalTomas(resolvedCount);

        if (typeof response.data.hasMore === 'boolean') {
          setHasMoreTomas(response.data.hasMore);
        } else if (totalFromResponse !== null) {
          setHasMoreTomas(totalFromResponse > TOMAS_PAGE_SIZE);
        } else {
          setHasMoreTomas(pagedCount > TOMAS_PAGE_SIZE);
        }

        // Use lastTomaDate from the response (already calculated on backend)
        if (response.data.lastTomaDate) {
          setLastTomaDate(new Date(response.data.lastTomaDate));
        } else {
          setLastTomaDate(null);
        }
      }
    } catch (error) {
      console.error('Error fetching basic tomas info:', error);
      await fetchTomasFromFirestore();
    }
  };

  const loadTomas = async (reset: boolean = false) => {
    if (reset) {
      setLoadingTomas(true);
    } else {
      if (loadingMoreTomas) return;
      setLoadingMoreTomas(true);
    }

    try {
      const offset = reset ? 0 : tomas.length;

      const response = await api.get(
        `/api/clients/${client.uid}/tomas?detailed=true&limit=${TOMAS_PAGE_SIZE}&offset=${offset}`
      );

      if (response.data.success) {
        const tomasData: TomaData[] = (response.data.tomas || []).map((toma: any) => ({
          ...toma
        }));

        if (reset) {
          setTomas(tomasData);
        } else {
          setTomas(prev => {
            const existingKeys = new Set(prev.map(item => `${item.tomaNumber}_${item.tableName}`));
            const merged = tomasData.filter((item: TomaData) => !existingKeys.has(`${item.tomaNumber}_${item.tableName}`));
            return [...prev, ...merged];
          });
        }

        const totalFromResponse = typeof response.data.totalTomas === 'number' ? response.data.totalTomas : null;
        const total = totalFromResponse ?? (reset ? tomasData.length : Math.max(totalTomas, offset + tomasData.length));
        const hasMoreFlag = response.data.hasMore ?? (offset + tomasData.length < total);

        setTotalTomas(total);
        setTomasCount(total);
        setHasMoreTomas(hasMoreFlag);

        if (reset) {
          if (response.data.lastTomaDate) {
            setLastTomaDate(new Date(response.data.lastTomaDate));
          } else if (tomasData.length > 0 && tomasData[0].fecha) {
            setLastTomaDate(new Date(tomasData[0].fecha));
          } else {
            setLastTomaDate(null);
          }
        }
      } else {
        if (reset) {
          setTomas([]);
          setTotalTomas(0);
          setHasMoreTomas(false);
        }
      }
    } catch (error) {
      console.error('💥 Error fetching detailed tomas info:', error);
      if (reset) {
        await fetchTomasFromFirestore();
      }
    } finally {
      if (reset) {
        setLoadingTomas(false);
      } else {
        setLoadingMoreTomas(false);
      }
    }
  };

  const handleCopyClientId = () => {
    navigator.clipboard.writeText(client.uid);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleGoToClient = () => {
    setIsInAdminMode(false);
    // PT-#87: selección completa (enable_tags/enable_sku incluidos). Sin los flags, los
    // consumidores (`enable_tags !== false`) trataban a Adidas como cliente con tags.
    setSelectedClient(buildClientSelection(client.uid, client));
  };

  const handleDeleteAllTomas = async () => {
    const confirmMessage = `¿Estás ABSOLUTAMENTE SEGURO de que deseas ELIMINAR TODAS LAS TOMAS del cliente "${client.display_name || client.name}"?\n\nEsta acción:\n- Eliminará TODAS las tablas toma_*_${client.uid}\n- NO se puede deshacer\n- Solo afectará a las tomas de este cliente específico\n\nEscribe "ELIMINAR" para confirmar:`;

    const userInput = prompt(confirmMessage);

    if (userInput !== 'ELIMINAR') {
      alert('Operación cancelada. No se eliminó nada.');
      return;
    }

    try {

      const response = await api.delete(`/api/clients/${client.uid}/delete-all-tomas`, {
        headers: {
          'x-client-id': client.uid,
          'x-client-name': client.display_name || client.name
        }
      });

      if (response.data.success) {
        alert(`✅ ${response.data.message}\n\nTablas eliminadas: ${response.data.deletedTables}`);
        // Refresh data
        onRefresh();
        fetchBasicTomasInfo();
        loadTomas(true);
      } else {
        alert(`❌ Error: ${response.data.error || 'No se pudieron eliminar las tomas'}`);
      }
    } catch (error: any) {
      console.error('💥 Error eliminando tomas:', error);
      const message = error?.response?.data?.error || error?.message || 'Error al eliminar las tomas';
      const details = error?.response?.data?.details;
      alert(details ? `${message}: ${details}` : message);
    }
  };

  const handleMenuAction = (action: string) => {
    setMenuOpen(false);
    switch(action) {
      case 'add-tomas':
        setImportTomasModalOpen(true);
        break;
      case 'manage-users':
        setUsersModalOpen(true);
        break;
      case 'edit':
        setEditModalOpen(true);
        break;
      case 'calculate-tags':
        setTagsModalOpen(true);
        break;
      case 'configure-columns':
        setColumnConfigModalOpen(true);
        break;
      case 'catalogado-prompt':
        break;
      case 'clustering':
        break;
      case 'delete-tomas':
        handleDeleteAllTomas();
        break;
      case 'delete':
        break;
    }
  };

  // Formato estándar de fecha del proyecto: DD/MM/YYYY (con HH:mm si hay hora real).
  const formatDate = (date: Date | string | null) => {
    if (!date) return t('admin.clientCard.noTomas');
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return 'Sin fecha';
      const hasTime = dateObj.getUTCHours() !== 0 || dateObj.getUTCMinutes() !== 0;
      const opts: Intl.DateTimeFormatOptions = {
        day: '2-digit', month: '2-digit', year: 'numeric',
      };
      if (hasTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; opts.hour12 = false; }
      return new Intl.DateTimeFormat('es-AR', opts).format(dateObj);
    } catch (error) {
      return 'Sin fecha';
    }
  };

  const formatDateTime = (input: Date | string | null, withTime: boolean) => {
    if (!input) return null;
    try {
      const d = typeof input === 'string' ? new Date(input) : input;
      if (isNaN(d.getTime())) return null;
      const opts: Intl.DateTimeFormatOptions = {
        day: '2-digit', month: '2-digit', year: '2-digit',
      };
      if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; opts.hour12 = false; }
      return new Intl.DateTimeFormat('es-AR', opts).format(d);
    } catch { return null; }
  };

  const daysAgoLabel = (date: Date | null): string | null => {
    if (!date) return null;
    const days = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (days <= 0) return t('admin.clientCard.today', 'hoy');
    if (days === 1) return t('admin.clientCard.dayAgo', '1 día');
    return t('admin.clientCard.daysAgo', '{{days}} días', { days });
  };

  const lastSyncDate = client.last_sync_at ? new Date(client.last_sync_at) : null;
  // lastTomaDate puede venir solo con fecha (12:00) o con hora real
  const lastTomaHasTime = lastTomaDate
    ? (lastTomaDate.getUTCHours() !== 0 || lastTomaDate.getUTCMinutes() !== 0)
    : false;

  // Código de colores compartido entre fecha y pastilla de tomas:
  //   <30d → verde · 30-90d → ámbar · >90d → rojo
  const ageBucket: 'fresh' | 'stale' | 'old' | 'none' = (() => {
    if (!lastTomaDate) return 'none';
    const ageDays = (Date.now() - lastTomaDate.getTime()) / 86400000;
    if (ageDays > 90) return 'old';
    if (ageDays > 30) return 'stale';
    return 'fresh';
  })();
  const lastTomaColorClass = ({
    fresh: 'text-emerald-700',
    stale: 'text-amber-600',
    old: 'text-red-700',
    none: 'text-gray-800',
  } as const)[ageBucket];

  // Pastilla de tomas:
  //   error de sync → fondo rojo fuerte + borde negro (alarma)
  //   0 tomas (cliente nuevo / sin sync exitoso) → azul (default)
  //   >0 tomas + bucket por antigüedad de última toma (verde / ámbar / rojo suave)
  const tomaPill = (() => {
    if (client.tableError === true) {
      return { wrap: 'bg-red-600 border-2 border-gray-900', icon: 'text-white', text: 'text-white', isError: true };
    }
    if (!tomasCount || tomasCount === 0) {
      return { wrap: 'bg-blue-50 border-blue-100', icon: 'text-blue-600', text: 'text-blue-900', isError: false };
    }
    if (ageBucket === 'old') return { wrap: 'bg-red-50 border-red-200', icon: 'text-red-600', text: 'text-red-700', isError: false };
    if (ageBucket === 'stale') return { wrap: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', text: 'text-amber-900', isError: false };
    if (ageBucket === 'fresh') return { wrap: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-600', text: 'text-emerald-900', isError: false };
    return { wrap: 'bg-blue-50 border-blue-100', icon: 'text-blue-600', text: 'text-blue-900', isError: false };
  })();

  return (
    <>
      <div
        className={`bg-white rounded-lg shadow hover:shadow-md transition-all duration-200 border border-gray-200 ${
          menuOpen ? 'overflow-visible' : 'overflow-hidden'
        }`}
      >
        {/* Compact View — 3 sectores claros (NO clickeable: usar chevron de Herramientas) */}
        <div className="flex items-center gap-4 py-4 pl-4 pr-5 hover:bg-gray-50 transition-colors">
          {/* SECTOR 1: Identidad — ancho fijo 320px para alineación entre cards */}
          <div className="flex items-center gap-3 w-[320px] flex-shrink-0">
            <div className="flex-shrink-0 w-36 h-14 flex items-center justify-center bg-gray-50 rounded border border-gray-200 px-2">
              {client.logo ? (
                <img
                  src={client.logo}
                  alt={client.display_name || client.name}
                  className="object-contain max-h-10 max-w-full"
                />
              ) : (
                <Building2 className="w-7 h-7 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate leading-tight">
                {client.display_name || client.name}
              </h3>
              <div className="flex items-center gap-1 mt-0.5">
                <p className="text-xs font-mono text-gray-400 truncate">{client.uid}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleCopyClientId(); }}
                  className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  title={t('admin.clientCard.expanded.copyClientId')}
                >
                  {copiedId
                    ? <Check className="w-3.5 h-3.5 text-green-500" />
                    : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          {/* SECTOR 2a: Pastilla de tomas — color según antigüedad. Durante sync,
               el número se borra y aparece skeleton pulsante para señalar actividad. */}
          <div className="hidden md:flex w-[130px] flex-shrink-0" style={{ marginLeft: '4em' }}>
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg w-full ${tomaPill.wrap} ${(panelSyncing || syncingNow) ? 'animate-pulse' : ''}`}>
              {tomaPill.isError
                ? <AlertCircle className={`w-4 h-4 flex-shrink-0 ${tomaPill.icon}`} />
                : <Database className={`w-4 h-4 flex-shrink-0 ${tomaPill.icon}`} />}
              {(panelSyncing || syncingNow) ? (
                /* Skeleton: barra ancha gris con pulso, sin número viejo confuso */
                <span className={`flex-1 h-3 rounded ${tomaPill.icon.replace('text-', 'bg-')} opacity-30`} />
              ) : client.tableError ? (
                <span className={`text-sm font-semibold ${tomaPill.text}`} title={client.tableErrorMessage || ''}>
                  {t('admin.clientCard.error')}
                </span>
              ) : (
                <span className={`text-sm font-bold ${tomaPill.text}`}>
                  {t('admin.clientCard.tomasCount', { count: tomasCount })}
                </span>
              )}
            </div>
          </div>

          {/* SECTOR 2b: Fechas — ancho fijo, alineadas en columna entre cards */}
          <div className="hidden md:flex flex-col gap-1 w-[290px] flex-shrink-0">
            {/* Última toma */}
            <div
              className="flex items-center gap-2 text-xs"
              title={t('admin.clientCard.lastTomaTooltip', 'Fecha de la captura más reciente disponible para este cliente.')}
            >
              <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {panelSyncing ? (
                <span className="text-blue-600 font-medium">{t('admin.clientCard.updatingTomas', 'Actualizando tomas...')}</span>
              ) : (
                <>
                  <span className="text-gray-500 w-[80px] flex-shrink-0">{t('admin.clientCard.lastToma')}:</span>
                  <span className={`font-semibold tabular-nums ${lastTomaColorClass}`}>
                    {formatDateTime(lastTomaDate, lastTomaHasTime) || '—'}
                  </span>
                  {lastTomaDate && (
                    <span className="text-[11px] text-gray-400">({daysAgoLabel(lastTomaDate)})</span>
                  )}
                </>
              )}
            </div>

            {/* Último sync */}
            <div
              className="flex items-center gap-2 text-xs"
              title={t('admin.clientCard.lastSyncTooltip', 'Fecha del último sync de metadata con Firestore.')}
            >
              <RefreshCw className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-gray-500 w-[80px] flex-shrink-0">{t('admin.clientCard.lastSync', 'Último sync')}:</span>
              <span className="font-semibold text-gray-800 tabular-nums">
                {formatDateTime(lastSyncDate, true) || t('admin.clientCard.never', 'nunca')}
              </span>
              {lastSyncDate && (
                <span className="text-[11px] text-gray-400">({daysAgoLabel(lastSyncDate)})</span>
              )}
            </div>
          </div>

          {/* Spacer para empujar herramientas a la derecha */}
          <div className="flex-1" />

          {/* SECTOR 3: Herramientas — botonera icon-only consistente */}
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 1. Ir a Cliente — primero, con texto compacto */}
            <button
              onClick={(e) => { e.stopPropagation(); handleGoToClient(); }}
              aria-label={t('admin.clientCard.goToClient')}
              className="flex items-center gap-1 leading-none px-2 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded transition-colors shadow-sm"
              title={t('admin.clientCard.goToClient', 'Ir a Cliente')}
            >
              <ExternalLink className="w-4 h-4" />
              <span>Ir a Cliente</span>
            </button>

            {/* 2. Sincronizar Tomas */}
            <button
              onClick={handleSyncNow}
              disabled={syncingNow || panelSyncing}
              aria-label={t('admin.clientCard.syncNowTooltip', 'Sincronizar Tomas')}
              className="flex items-center justify-center p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 border border-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('admin.clientCard.syncNowTooltip', 'Sincronizar metadata de tomas de este cliente con Firestore.')}
            >
              {syncingNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>

            {/* 3. Administrar Cliente */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.history.pushState(null, '', `/?editClient=${encodeURIComponent(client.uid)}`);
                // Forzar a ClientsManagement a re-leer URL: dispatch popstate
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              aria-label="Administrar Cliente"
              className="flex items-center justify-center p-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              title="Administrar Cliente — abrir configuración avanzada"
            >
              <Settings className="w-4 h-4" />
            </button>

          {/* DEPRECATED: Menu Dropdown — reemplazado por botón Config + Admin Cliente */}
          {false && (
            <div className="relative flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
              title={t('admin.clientCard.options')}
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-20 py-1 border border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('add-tomas');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.addTomas')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('manage-users');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Users className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.manageUsers')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('edit');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Edit className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.editClient')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('configure-columns');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Settings className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.configureColumns')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('calculate-tags');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Tag className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.calculateTags')}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('catalogado-prompt');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Sparkles className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.catalogPrompt')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('clustering');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
                  >
                    <Network className="w-4 h-4 text-gray-600" />
                    {t('admin.clientCard.menu.clustering')}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('delete-tomas');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm text-orange-600"
                  >
                    <Database className="w-4 h-4" />
                    {t('admin.clientCard.menu.deleteTomas')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuAction('delete');
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-sm text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('admin.clientCard.menu.deleteClient')}
                  </button>
                </div>
              </>
            )}
          </div>
          )}

          </div>
        </div>
        {/* Accordion eliminado (T-J): Conexión/Historial/Usuarios eran redundantes.
            Historial de Tomas + acción Calcular catalogado viven ahora en
            TomasHistoryList. Usuarios se gestionan desde UsersModal. */}
      </div>

      {/* Modals */}
      {usersModalOpen && (
        <UsersModal
          client={client}
          onClose={() => setUsersModalOpen(false)}
        />
      )}

      {importTomasModalOpen && (
        <ImportTomasModal
          client={client}
          onClose={() => setImportTomasModalOpen(false)}
            onImportComplete={() => {
            setImportTomasModalOpen(false);
            onRefresh();
            // Refresh both basic and detailed tomas info
            fetchBasicTomasInfo();
            loadTomas(true);
          }}
        />
      )}

      {editModalOpen && (
        <EditClientModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          client={client}
          onSave={(patch) => {
            setEditModalOpen(false);
            if (patch && onClientPatch) {
              onClientPatch(client.uid, patch);
            } else {
              onRefresh();
            }
          }}
        />
      )}

      {tagsModalOpen && (
        <TagsProcessingModal
          clientId={client.uid}
          clientName={client.display_name || client.name}
          onClose={() => setTagsModalOpen(false)}
          onCompleted={() => {
            fetchBasicTomasInfo();
            loadTomas(true);
          }}
        />
      )}

      {columnConfigModalOpen && (
        <ColumnConfigModal
          isOpen={columnConfigModalOpen}
          clientId={client.uid}
          clientName={client.display_name || client.name}
          onClose={() => setColumnConfigModalOpen(false)}
        />
      )}
    </>
  );
};

export default ClientCard;
